import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { MagnifyingGlass, ArrowsClockwise, ImageBroken, EyeSlash } from '@phosphor-icons/react';
import { fetchProducts, fetchCategories, syncProducts, toggleProductActive, getFileUrl } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

function ProductCard({ product, isAdmin, onToggleActive }) {
  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState(false);
  const isInactive = product.active === false;

  return (
    <div
      data-testid={`product-card-${product.id}`}
      onClick={() => navigate(`/product/${product.id}`)}
      className={`bg-white rounded-xl border overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group animate-fade-in ${isInactive ? 'border-[#FCA5A5] opacity-60' : 'border-[#E2E8F0] hover:border-[#BAE6FD]'}`}
    >
      <div className="aspect-square bg-[#F8FAFC] flex items-center justify-center p-4 border-b border-[#F1F5F9] relative">
        {product.image_url && !imgErr ? (
          <img src={getFileUrl(product.image_url)} alt={product.name}
            className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
            onError={() => setImgErr(true)} />
        ) : (
          <ImageBroken size={48} className="text-[#CBD5E1]" />
        )}
        {isInactive && (
          <div className="absolute top-2 right-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">Inactive</div>
        )}
        {isAdmin && (
          <button
            data-testid={`toggle-active-${product.id}`}
            onClick={(e) => { e.stopPropagation(); onToggleActive(product.id); }}
            className={`absolute top-2 left-2 w-7 h-7 rounded-lg flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 ${isInactive ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-red-50 text-red-400 hover:bg-red-100'}`}
            title={isInactive ? 'Activate' : 'Deactivate'}
          >
            <EyeSlash size={14} weight="bold" />
          </button>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-[#0F172A] line-clamp-2 mb-1 group-hover:text-[#2563EB] transition-colors" style={{ fontFamily: 'Outfit' }}>
          {product.name}
        </h3>
        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0]">
          {product.category}
        </span>
      </div>
    </div>
  );
}

export default function CatalogPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [total, setTotal] = useState(0);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchProducts({ search, category, limit: 200, show_inactive: showInactive });
      setProducts(data.products);
      setTotal(data.total);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  }, [search, category, showInactive]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { fetchCategories().then(setCategories).catch(() => {}); }, []);

  const handleSync = async () => {
    const ok = window.confirm(
      "Check for new products from the vendor catalog?\n\n" +
      "• NEW products will be added.\n" +
      "• Existing products (images, descriptions, prices, etc.) are NEVER modified.\n\n" +
      "This does NOT refresh prices — use 'Re-sync Prices' in Admin for that."
    );
    if (!ok) return;
    setSyncing(true);
    try {
      const r = await syncProducts();
      toast.success(`Checked: ${r.products_found} found, ${r.new_products} new added`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const handleToggleActive = async (productId) => {
    try {
      const r = await toggleProductActive(productId);
      toast.success(r.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]" data-testid="catalog-page">
      <Sidebar />
      <main className="flex-1 p-6 md:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: 'Outfit' }}>Products</h1>
              <p className="text-[#475569] mt-1">{total} products in catalog</p>
            </div>
            <Button data-testid="sync-btn" onClick={handleSync} disabled={syncing}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg h-10 px-5 font-semibold flex items-center gap-2 transition-all">
              <ArrowsClockwise size={18} weight="bold" className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Syncing...' : 'Check for New Products'}
            </Button>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input data-testid="search-input" placeholder="Search products..." value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-10 rounded-lg border-[#E2E8F0] bg-white focus:ring-2 focus:ring-[#3B82F6]" />
            </div>
            <Select value={category} onValueChange={v => setCategory(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="category-filter" className="w-[200px] h-10 rounded-lg border-[#E2E8F0]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {isAdmin && (
              <label className="flex items-center gap-2 cursor-pointer h-10 px-3 rounded-lg border border-[#E2E8F0] bg-white text-sm">
                <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
                  data-testid="show-inactive-toggle" className="rounded" />
                <span className="text-[#475569]">Show Inactive</span>
              </label>
            )}
          </div>

          {/* Product Grid */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[...Array(15)].map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-xl" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 animate-fade-in">
              <h3 className="text-lg font-semibold text-[#0F172A] mb-1" style={{ fontFamily: 'Outfit' }}>No products found</h3>
              <p className="text-[#94A3B8] text-sm">Adjust filters or sync vendors.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {products.map(p => <ProductCard key={p.id} product={p} isAdmin={isAdmin} onToggleActive={handleToggleActive} />)}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
